import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@store/index';
import { extractSearchPages } from '@store/search';

import { applicationsAPI, DEFAULT_ASSETS_SORT } from '../applications.api';
import type { Application, ApplicationVersion } from '../applications.models';

import { applicationTag, applicationVersionTag } from './cache-tags';

/**
 * Scan every cached entry of `endpointName` that provides `tag` — regardless
 * of sort or filter args — and return the first row whose id matches.
 */
const findCachedRow = <T extends { systemMetadata?: { id?: string } }>(
  state: RootState,
  endpointName: 'listApplications' | 'listApplicationVersions',
  tag: ReturnType<typeof applicationTag | typeof applicationVersionTag>,
  id: string,
): T | undefined =>
  applicationsAPI.util
    .selectInvalidatedBy(state, [tag])
    .filter(entry => entry.endpointName === endpointName)
    .map(entry => state.api.queries[entry.queryCacheKey])
    .filter(cacheEntry => cacheEntry?.status === 'fulfilled')
    .flatMap(cacheEntry =>
      extractSearchPages(
        cacheEntry?.data as Parameters<typeof extractSearchPages<T>>[0],
      ),
    )
    .find(({ systemMetadata }) => systemMetadata?.id === id);

const {
  endpoints: {
    getApplication: { useQuery: useGetApplicationEndpointQuery },
    getApplicationVersion: { useQuery: useGetApplicationVersionEndpointQuery },
    getAsset: { useQuery: useGetAssetEndpointQuery },
    listAssets: { useInfiniteQueryState: useListAssetsInfiniteQueryState },
  },
} = applicationsAPI;

/**
 * Returns getApplication state, falling back to any cached `listApplications`
 * entry to avoid a network fetch when navigating from the list.
 */
export const useGetApplicationQuery = (id: string) => {
  const application = useSelector((state: RootState) =>
    id
      ? findCachedRow<Application>(
          state,
          'listApplications',
          applicationTag(id),
          id,
        )
      : undefined,
  );

  const result = useGetApplicationEndpointQuery(id, { skip: !id });

  const data = useMemo(
    () => result.currentData ?? application,
    [result.currentData, application],
  );

  // Suppress loading when the list cache already provides data, so
  // consumers don't flash a spinner while the detail query catches up.
  const isLoading = result.isLoading && !application;
  const isFetching = result.isFetching && !application;

  return { ...result, data, isFetching, isLoading };
};

/**
 * Returns getApplicationVersion state, falling back to any cached
 * `listApplicationVersions` entry, plus a name-only fallback that scans
 * cached `listApplications` entries for the row's `latestVersion`.
 */
export const useGetApplicationVersionQuery = (
  appId: string,
  versionId: string,
) => {
  const version = useSelector((state: RootState) =>
    appId && versionId
      ? findCachedRow<ApplicationVersion>(
          state,
          'listApplicationVersions',
          applicationVersionTag(appId, versionId),
          versionId,
        )
      : undefined,
  );

  const latestVersionName = useSelector((state: RootState) => {
    if (!appId || !versionId) return undefined;
    const app = findCachedRow<Application>(
      state,
      'listApplications',
      applicationTag(appId),
      appId,
    );
    return app?.latestVersion?.id === versionId
      ? app.latestVersion.name
      : undefined;
  });

  const result = useGetApplicationVersionEndpointQuery(
    { appId, versionId },
    { skip: !appId || !versionId },
  );

  const data = useMemo(
    () => result.currentData ?? version,
    [result.currentData, version],
  );

  const hasCachedData = !!version || !!latestVersionName;
  const isLoading = result.isLoading && !hasCachedData;
  const isFetching = result.isFetching && !hasCachedData;

  return {
    ...result,
    data,
    isFetching,
    isLoading,
    versionName: data?.name ?? latestVersionName,
  };
};

/**
 * Returns getAsset state, falling back to the listAssets cache to
 * avoid a network fetch.
 */
export const useGetAssetQuery = (
  appId: string,
  versionId: string,
  assetId: string,
) => {
  const { asset } = useListAssetsInfiniteQueryState(
    { appId, sort: DEFAULT_ASSETS_SORT, versionId },
    {
      selectFromResult: ({ data }) => ({
        asset: extractSearchPages(data).find(
          ({ systemMetadata }) => systemMetadata?.id === assetId,
        ),
      }),
    },
  );

  const result = useGetAssetEndpointQuery(
    { appId, versionId, assetId },
    { skip: !appId || !versionId || !assetId },
  );

  const data = useMemo(
    () => result.currentData ?? asset,
    [result.currentData, asset],
  );

  const isLoading = result.isLoading && !asset;
  const isFetching = result.isFetching && !asset;

  return { ...result, data, isFetching, isLoading };
};
